import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { toast } from 'sonner';
import type {
  ProposedDataChange,
  ProposedEntityType,
} from '@logspot-platform/platform-types';
import { dataChangesOptions } from '@/hooks/query-options';
import { DataManagementService } from '@/api/data-management-service';
import { ApiError } from '@/api/api-client';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/data/changes',
)({
  component: SuperAdminDataChangesPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

const ENTITY_LABELS: Record<ProposedEntityType, string> = {
  persona: 'Persona',
  company_enrichment: 'Company Record',
  agent: 'Agent',
};

// Renders the staged value as stable, pretty JSON for the before/after diff.
const formatSnapshot = (value: Record<string, unknown> | null): string =>
  value ? JSON.stringify(value, null, 2) : '— (new record)';

function ChangeCard({ change }: { change: ProposedDataChange }) {
  const queryClient = useQueryClient();
  const [confirmDismissOpen, setConfirmDismissOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['data-changes'] });

  const applyMutation = useMutation({
    mutationFn: () => DataManagementService.applyChange(change.id),
    onSuccess: () => {
      void invalidate();
      // The applied entity's own list may now be stale.
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      void queryClient.invalidateQueries({ queryKey: ['attributes'] });
      toast.success('Change applied.');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError ? error.message : 'Failed to apply change.';
      toast.error(message);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: () => DataManagementService.dismissChange(change.id),
    onSuccess: () => {
      void invalidate();
      toast.success('Change dismissed.');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError ? error.message : 'Failed to dismiss change.';
      toast.error(message);
    },
  });

  const isPending = applyMutation.isPending || dismissMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">
            {ENTITY_LABELS[change.entity_type]}: {change.entity_key}
          </CardTitle>
          <Badge
            variant={change.change_kind === 'silent' ? 'neutral' : 'indigo'}
          >
            {change.change_kind}
          </Badge>
          <Badge variant="neutral">{change.scope}</Badge>
        </div>
        <CardDescription>
          Proposed by {change.proposed_by ?? 'unknown'} ·{' '}
          {new Date(change.created_at).toLocaleString()}
          {change.note ? ` · ${change.note}` : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs font-medium uppercase">
              Before
            </span>
            <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs">
              {formatSnapshot(change.base_snapshot)}
            </pre>
          </div>
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs font-medium uppercase">
              After
            </span>
            <pre className="bg-muted max-h-64 overflow-auto rounded-md p-3 text-xs">
              {formatSnapshot(change.proposed)}
            </pre>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-end gap-2">
        <Button
          variant="outline"
          disabled={isPending}
          onClick={() => setConfirmDismissOpen(true)}
        >
          Dismiss
        </Button>
        <Button
          variant="primary"
          disabled={isPending}
          onClick={() => applyMutation.mutate()}
        >
          Apply
        </Button>
      </CardFooter>
      <ConfirmModal
        open={confirmDismissOpen}
        variant="destructive"
        title="Dismiss Change?"
        message="This discards the staged proposal. It cannot be applied later."
        confirmText="Dismiss"
        loading={dismissMutation.isPending}
        onCancel={() => setConfirmDismissOpen(false)}
        onFinish={() => {
          setConfirmDismissOpen(false);
          dismissMutation.mutate();
        }}
      />
    </Card>
  );
}

// Super-admin review queue for staged data-management proposals. Each card
// shows a before/after diff and apply/dismiss actions; apply is drift-checked
// server-side and surfaces a 409 as a toast.
function ProposedChangesPanel() {
  const { data: changes = [], isLoading } = useQuery(dataChangesOptions());

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading changes…</p>;
  }

  if (changes.length === 0) {
    return <p className="text-muted-foreground text-sm">No pending changes.</p>;
  }

  return (
    <div className="space-y-4">
      {changes.map((change) => (
        <ChangeCard key={change.id} change={change} />
      ))}
    </div>
  );
}

function SuperAdminDataChangesPage() {
  return (
    <>
      <PageHeader
        title="Data Changes"
        description="Review and apply staged data updates."
      />
      <PageContent>
        <ProposedChangesPanel />
      </PageContent>
    </>
  );
}
